import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiEndpoint } from 'app/agriculture/queries/apiEndpoints';
import { Invitation, invitationSchema } from 'app/agriculture/queries/invitations/types/Invitation';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { use } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import { useFarm } from 'app/agriculture/hooks/useFarm/useFarm';
import { addToCache } from 'app/agriculture/mutations/utils/addToCache';

interface NewUser {
    email: string;
}

export function useInviteUsersMutation() {
    const { agricultureToastService } = use(ServicesContext);
    const instance = createAxiosInstance();
    const { farm } = useFarm();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async function (users: NewUser[]) {
            const promises = users.map((user) =>
                instance.post<unknown>(ApiEndpoint.invitations, {
                    ...user,
                    farm_id: farm.id,
                }),
            );

            return await Promise.all(promises).then((responses) =>
                responses.map(({ data }) => invitationSchema.parse(data) as Invitation),
            );
        },
        onSuccess: async function (invitations: Invitation[]) {
            // add to the cache for the farm invitations query
            addToCache(invitations, {
                queryClient,
                queryKey: [ApiEndpoint.invitations, { farmId: farm.id }],
            });

            // add to the cache for each of the user invitations queries
            invitations.forEach((invitation) => {
                addToCache([invitation], {
                    queryClient,
                    queryKey: [ApiEndpoint.invitations, { email: invitation.email }],
                });
            });

            await agricultureToastService.showToast({
                title: 'Invitations sent',
                subtext: `Sent to: ${invitations.map(({ email }) => email).join(', ')}`,
                type: AgricultureToastType.SUCCESS,
                variant: 'detailed',
            });
        },
        onError: async function (error) {
            console.error('Error sending invitation', error);
            await agricultureToastService.showToast({
                title: 'Invitation Failed',
                type: AgricultureToastType.ERROR,
                variant: 'simple',
            });
        },
    });
}
